import { FeatureFlagName } from '@repo/shared/dtos/feature-flag'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import { useAsyncData } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { ReactNode, useCallback, useState } from 'react'
import { toast } from 'sonner'

import { DataTable } from '@/components/admin/members/data-table'
import { OrganizationEditor } from '@/components/sudo/organization/OrganizationEditor'
import { organizationsColumns } from '@/components/sudo/organization/organizations-columns'
import { OrganizationSnapshoter } from '@/components/sudo/organization/OrganizationSnapshoter'
import { altApi } from '@/main'
import { sudoStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { SudoContextOrganization } from '@/store/sudo'

export type ModalHandler = {
  modalHandler: {
    setOrganization: (value: SudoContextOrganization | null) => void
    organization: SudoContextOrganization | null
  }
}

export function OrganizationList({
  children,
  organizations,
  title,
  description,
}: {
  children?: ReactNode
  organizations: SudoContextOrganization[]
  title: string
  description: string
}) {
  const dispatch = useStoreDispatch()
  const { t } = useT()

  const [open, setOpen] = useState<{
    action: 'edit' | 'snapshot'
    organization: SudoContextOrganization
  } | null>(null)

  const {
    data: [myOrganizations],
  } = useAsyncData([(state) => state.user.organizations])
  if (!myOrganizations) return null
  const myOrganizationIds = myOrganizations.map((org) => org.id)

  const handleToggleOrganization = useCallback(
    async (params: {
      featureFlagKey: FeatureFlagName
      organizationId: string
      enabled: boolean
    }) => {
      await altApi.featureFlags.toggleOrganization(params)
      void dispatch(sudoStoreActions.initSudo())
      toast.success(t('notifications.xSuccess'))
    },
    [],
  )

  const handleEditOrganization = useCallback((organization: SudoContextOrganization | null) => {
    setOpen(organization ? { action: 'edit', organization } : null)
  }, [])

  const handleTakeSnapshot = useCallback((organization: SudoContextOrganization | null) => {
    setOpen(organization ? { action: 'snapshot', organization } : null)
  }, [])

  const columns = organizationsColumns({
    myOrganizationIds,
    edit: handleEditOrganization,
    toggleOrganization: handleToggleOrganization,
    takeSnapshot: handleTakeSnapshot,
  })
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
        <CardAction>{children}</CardAction>
      </CardHeader>
      <CardContent>
        <OrganizationEditor
          modalHandler={{
            organization: open?.action === 'edit' ? open.organization : null,
            setOrganization: handleEditOrganization,
          }}
        />

        <OrganizationSnapshoter
          modalHandler={{
            organization: open?.action === 'snapshot' ? open.organization : null,
            setOrganization: handleTakeSnapshot,
          }}
        />

        <DataTable
          columns={columns}
          data={organizations}
          initialState={{
            sorting: [
              {
                id: 'name',
                desc: false,
              },
            ],
          }}
        />
      </CardContent>
    </Card>
  )
}
